// File: frontend/src/pages/Login.js
// Version: 3.0 (Task T3 Screen 9 — Design System Primitives & Bilingual Support)
// Description: Login page migrated to unified design system components (Card, Input, Button, Label)
//              and tokens, with full bilingual EN/RU support via useUiLanguage and useBranding.

import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useUiLanguage } from '../context/UiLanguageContext';
import UiLanguageToggle from '../components/UiLanguageToggle';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import './Login.css';

const Login = () => {
    const { login } = useAuth();
    const { branding, isFitenko } = useBranding();
    const { t } = useUiLanguage();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const loginToast = toast.loading(t('practice.login.toast_signing_in'));

        try {
            await login(email, password);
            toast.success(t('practice.login.toast_success'), { id: loginToast });
            // Navigation to /dashboard occurs automatically in App.js via auth state tracking
        } catch (err) {
            const errorMessage = err?.message || t('practice.login.toast_error');
            toast.error(errorMessage, { id: loginToast });
        } finally {
            setLoading(false);
        }
    };

    const heading = isFitenko
        ? t('practice.login.fitenko_title')
        : t('practice.login.title', { company: branding?.company_name || 'Legal OS' });

    const subtitle = isFitenko
        ? t('practice.login.fitenko_subtitle')
        : t('practice.login.subtitle');

    return (
        <div className={`login-page-background${isFitenko ? ' login--fitenko' : ''}`}>
            <div className="login-top-bar">
                <UiLanguageToggle />
            </div>

            <Card
                className="login-card"
                style={{
                    boxShadow: 'var(--shadow-lg)',
                    borderRadius: 'var(--radius-card)',
                    ...(isFitenko ? { background: 'transparent', boxShadow: 'none', border: 'none' } : {})
                }}
            >
                <div className="logo-container" aria-hidden="true">
                    {isFitenko ? (
                        <svg
                            className="logo-lock"
                            viewBox="0 0 24 24"
                            width="28"
                            height="28"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                    ) : (
                        <span className="logo-text">{branding?.instance_name?.toUpperCase() || 'OS'}</span>
                    )}
                </div>

                <h2 className="welcome-text">{heading}</h2>
                <p className="subtitle-text">{subtitle}</p>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <Label htmlFor="email">{t('practice.login.email_label')}</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={loading}
                            placeholder={t('practice.login.email_placeholder')}
                            autoComplete="email"
                        />
                    </div>

                    <div className="form-group">
                        <Label htmlFor="password">{t('practice.login.password_label')}</Label>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            placeholder={t('practice.login.password_placeholder')}
                            autoComplete="current-password"
                        />
                    </div>

                    <Button
                        type="submit"
                        variant="primary"
                        disabled={loading}
                        loading={loading}
                        className="signin-button"
                        style={{ fontWeight: 600 }}
                    >
                        {loading ? t('practice.login.signing_in') : (isFitenko ? t('practice.login.signin_continue') : t('practice.login.signin'))}
                    </Button>
                </form>
            </Card>
        </div>
    );
};

export default Login;
